import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe, Mail, Send, Phone, MapPin, CheckCircle, XCircle } from 'lucide-react';
import Footer from './Footer';
import SEO from './SEO';

const API_BASE_URL = (() => {
  const url = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/+$/, '');
  return url.endsWith('/api') ? url : `${url}/api`;
})();

const Contact = () => {
    const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setStatus(null);
        try {
            const res = await fetch(`${API_BASE_URL}/contact`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form),
            });
            if (res.ok) {
                setStatus('success');
                setForm({ name: '', email: '', subject: '', message: '' });
            } else {
                setStatus('error');
            }
        } catch {
            setStatus('error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-soft-blue">
            <SEO
                title="İletişim — Destek ve Sorularınız İçin Bize Ulaşın"
                description="CeviriPro ekibiyle iletişime geçin. Çeviri, hesap veya teknik konularda destek için destek@ceviripro.com adresine yazın ya da iletişim formunu doldurun."
                canonical="/iletisim"
            />

            {/* Nav */}
            <nav className="glass sticky top-0 z-50 border-b border-white/20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <Link to="/" className="flex items-center gap-2">
                            <div className="bg-gradient-to-r from-blue-500 to-indigo-500 p-2.5 rounded-2xl shadow-lg shadow-blue-500/30">
                                <Globe className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-xl font-bold text-gradient-blue">ÇeviriPro</span>
                        </Link>
                        <div className="flex items-center gap-4">
                            <Link to="/login" className="text-slate-600 hover:text-blue-600 font-medium transition-colors">Giriş Yap</Link>
                            <Link to="/register" className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-sm font-semibold rounded-xl hover:shadow-xl hover:shadow-blue-500/30 transition-all">
                                Ücretsiz Başla
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <section className="py-20 px-4 text-center">
                <span className="inline-block px-5 py-2.5 bg-white/80 backdrop-blur-sm border border-blue-200 rounded-full text-blue-700 text-sm font-medium mb-8 shadow-lg shadow-blue-500/10">
                    İletişim
                </span>
                <h1 className="text-4xl font-black text-slate-900 mb-4">Bizimle İletişime Geçin</h1>
                <p className="text-xl text-slate-600 max-w-xl mx-auto">Sorularınız, önerileriniz veya destek taleplerini için buradayız.</p>
            </section>

            {/* Contact Blocks + Form */}
            <section className="py-16 px-4">
                <div className="max-w-6xl mx-auto grid md:grid-cols-5 gap-12">
                    {/* Info */}
                    <div className="md:col-span-2 space-y-6">
                        <h2 className="text-2xl font-bold text-slate-900 mb-6">İletişim Bilgileri</h2>
                        {[
                            { icon: Mail, label: "Email", value: "destek@ceviripro.com", href: "mailto:destek@ceviripro.com" },
                            { icon: Phone, label: "Telefon", value: "+90 (212) 000 00 00", href: "tel:+902120000000" },
                            { icon: MapPin, label: "Adres", value: "İstanbul, Türkiye", href: null },
                        ].map(({ icon: Icon, label, value, href }) => (
                            <div key={label} className="card-soft p-5 rounded-2xl flex items-start gap-4">
                                <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                    <Icon className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <div className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1">{label}</div>
                                    {href ? (
                                        <a href={href} className="text-slate-700 font-medium hover:text-blue-600 transition-colors">{value}</a>
                                    ) : (
                                        <span className="text-slate-700 font-medium">{value}</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Form */}
                    <div className="md:col-span-3 bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/50 p-8">
                        <h2 className="text-2xl font-bold text-slate-900 mb-6">Mesaj Gönderin</h2>
                        {status === 'success' && (
                            <div className="flex items-center gap-3 p-4 bg-blue-50/80 backdrop-blur-sm border border-blue-200 rounded-2xl mb-6">
                                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                                <p className="text-blue-800 font-medium">Mesajınız iletildi! En kısa sürede yanıt vereceğiz.</p>
                            </div>
                        )}
                        {status === 'error' && (
                            <div className="flex items-center gap-3 p-4 bg-red-50/80 backdrop-blur-sm border border-red-200 rounded-2xl mb-6">
                                <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                                <p className="text-red-800 font-medium">Bir hata oluştu. Lütfen tekrar deneyin.</p>
                            </div>
                        )}
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Ad Soyad</label>
                                    <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                                        className="w-full px-4 py-3.5 rounded-2xl border-2 border-blue-100 focus:border-blue-400 focus:outline-none transition-all bg-white/50"
                                        placeholder="Adınız" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Email</label>
                                    <input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                                        className="w-full px-4 py-3.5 rounded-2xl border-2 border-blue-100 focus:border-blue-400 focus:outline-none transition-all bg-white/50"
                                        placeholder="email@ornek.com" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Konu</label>
                                <input required value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
                                    className="w-full px-4 py-3.5 rounded-2xl border-2 border-blue-100 focus:border-blue-400 focus:outline-none transition-all bg-white/50"
                                    placeholder="Nasıl yardımcı olabiliriz?" />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Mesajınız</label>
                                <textarea required rows={5} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
                                    className="w-full px-4 py-3.5 rounded-2xl border-2 border-blue-100 focus:border-blue-400 focus:outline-none transition-all bg-white/50 resize-none"
                                    placeholder="Detaylı açıklama..." />
                            </div>
                            <button type="submit" disabled={loading}
                                className="w-full py-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-bold rounded-2xl hover:shadow-xl hover:shadow-blue-500/30 transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                                {loading ? 'Gönderiliyor...' : (<><Send className="w-5 h-5" /> Mesaj Gönder</>)}
                            </button>
                        </form>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Contact;
